import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  useNavigate,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";

import appCss from "../styles.css?url";
import { AdBanner } from "@/components/AdBanner";
import { Toaster } from "@/components/ui/sonner";
import { useSession } from "@/hooks/useSession";
import { ENTITLEMENTS_QUERY_KEY } from "@/hooks/useEntitlements";
import { useConsentStatus } from "@/lib/legal";
import { setStatusBarStyle } from "@/lib/native";
import { syncFromServer } from "@/lib/notifications";
import { initPurchases } from "@/lib/purchases";
import { cloudRepository } from "@/lib/repository/cloud";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { getBrowserSupabase } from "@/lib/supabase-browser";
import { parseDeepLink } from "@/lib/oauth-browser";
import { guest } from "@/lib/guest";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      // interactive-widget=resizes-content: iOS 17.4+ Safari가 키보드 만큼 viewport 축소
      //   (Drawer/Sheet 등 bottom-anchored fixed 요소가 키보드 위로 올라타는 빈공간 문제 방지)
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=resizes-content" },
      { title: "탄단지 버블" },
      { name: "description", content: "탄·단·지 균형 체크 5초 트래커" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      // Pretendard — 한국어 SNS·UI 표준 폰트 (공유 카드 포함 전체에 적용)
      {
        rel: "stylesheet",
        href: "https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.css",
      },
      // 온글잎 박다현체 — public/fonts에서 self-host. Gowun Dodum CDN 제거.
      // @font-face는 src/styles.css에 정의됨.
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="ko">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const navigate = useNavigate();

  useEffect(() => {
    void setStatusBarStyle("dark");
  }, []);

  // OAuth SFSafariViewController 콜백 — app.tandanjibubble://auth/callback?code=...
  useEffect(() => {
    const listenerPromise = CapacitorApp.addListener("appUrlOpen", async ({ url }) => {
      const link = parseDeepLink(url);
      if (link.type !== "oauth_callback") return;
      try {
        await Browser.close();
        const supabase = getBrowserSupabase();
        const { error } = await supabase.auth.exchangeCodeForSession(link.code);
        if (error) throw error;
        // 게스트 모드 해제 (OAuth 로그인 성공)
        guest.disable();
        navigate({ to: link.next, replace: true });
      } catch (e) {
        console.error("[appUrlOpen] OAuth exchange failed", e);
        navigate({ to: "/auth/login", search: { error: (e as Error).message }, replace: true });
      }
    });
    return () => {
      void listenerPromise.then((l) => l.remove());
    };
  }, [navigate]);

  return (
    <QueryClientProvider client={queryClient}>
      <AppShell />
      <Toaster />
    </QueryClientProvider>
  );
}

/**
 * 라우트 가드 + 레이아웃.
 *  - /auth/*  : 인증 화면. BottomTabBar 숨김. 가드 X
 *  - /legal/* : 약관·정책 페이지 (회원가입 전 비로그인 접근 필요). 가드 X
 *  - 그 외    : 인증 필요. unauthenticated → /auth/login?next=<현재경로>
 */
function AppShell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // /auth/callback도 인증 가드 대상에서 제외 (OAuth fragment 처리 중)
  const isAuthRoute = pathname.startsWith("/auth/");
  const isLegalRoute = pathname.startsWith("/legal/");
  const isPublicRoute = isAuthRoute || isLegalRoute;
  const { session, status } = useSession();
  // Step 18 — consent 가드.
  //   인증 사용자에게만 hook이 의미 있음. enabled 분기 없이도 401이면 compliant=true
  //   반환하므로(legal.ts) 안전. authenticated 일 때만 missing redirect 수행.
  const { data: consent, isLoading: isConsentLoading } = useConsentStatus();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // 로그인 완료 시 서버 알림 설정을 기기 로컬 알림과 동기화 +
  // entitlements 캐시 무효화 (비로그인 시 401→free로 캐시된 값을 Pro/Basic으로 갱신).
  // 모든 로그인 경로(이메일·OAuth·세션 복원)를 한 곳에서 커버.
  useEffect(() => {
    if (status !== "authenticated" || !session) return;
    void syncFromServer(() => cloudRepository.userNotifications.get());
    void queryClient.invalidateQueries({ queryKey: ENTITLEMENTS_QUERY_KEY });
    // Step 13 — RevenueCat 초기화. iOS 네이티브에서만 동작(웹은 no-op).
    // appUserID = Supabase userId로 매핑 → webhook의 app_user_id와 일치.
    const rcKey = import.meta.env.VITE_RC_PUBLIC_API_KEY_IOS;
    if (rcKey) void initPurchases(session.userId, rcKey);
  }, [status, session?.userId, queryClient]);

  const isGuest = status === "unauthenticated" && guest.isEnabled();

  useEffect(() => {
    if (isPublicRoute) return;
    if (status === "unauthenticated" && !isGuest) {
      navigate({
        to: "/auth/login",
        search: pathname === "/" ? undefined : { next: pathname },
        replace: true,
      });
      return;
    }
    if (status === "authenticated" && consent && !consent.compliant) {
      navigate({
        to: "/auth/consent",
        search: pathname === "/" ? undefined : { next: pathname },
        replace: true,
      });
    }
  }, [isPublicRoute, status, pathname, navigate, consent, isGuest]);

  // auth·legal 화면은 가드 통과
  if (isPublicRoute) {
    return (
      <div
        className="min-h-screen w-full bg-white"
        style={{ paddingTop: "max(env(safe-area-inset-top), 1rem)" }}
      >
        <Outlet />
      </div>
    );
  }

  // 게스트 모드 — 홈만 허용, 로그인 배너 표시
  if (isGuest) {
    return (
      <>
        <div
          className="fixed inset-x-0 top-0 z-50 bg-white"
          style={{ height: "env(safe-area-inset-top)" }}
        />
        <div
          className="min-h-screen w-full bg-white"
          style={{
            paddingTop: "max(env(safe-area-inset-top), 1rem)",
            paddingBottom: "calc(10rem + var(--ad-banner-height, 0px))",
          }}
        >
          <GuestBanner />
          <Outlet />
        </div>
        <BottomTabBar />
      </>
    );
  }

  // 부팅 중이거나 리다이렉트가 확정된 상태
  if (
    status === "loading" ||
    status === "unauthenticated" ||
    isConsentLoading ||
    (status === "authenticated" && consent != null && !consent.compliant)
  ) {
    return <SplashScreen />;
  }

  if (status === "error") {
    return <AuthErrorScreen />;
  }

  // authenticated
  void session;
  return (
    <>
      <div
        className="fixed inset-x-0 top-0 z-50 bg-white"
        style={{ height: "env(safe-area-inset-top)" }}
      />
      <div
        className="min-h-screen w-full bg-white"
        style={{
          paddingTop: "max(env(safe-area-inset-top), 1rem)",
          paddingBottom: "calc(10rem + var(--ad-banner-height, 0px))",
        }}
      >
        <Outlet />
      </div>
      <BottomTabBar />
      <AdBanner />
    </>
  );
}

function GuestBanner() {
  return (
    <Link
      to="/auth/login"
      className="flex items-center justify-between bg-neutral-900 px-4 py-2.5 text-white"
    >
      <span className="text-xs">비로그인 상태 · 데이터가 저장되지 않아요</span>
      <span className="text-xs font-semibold">로그인 →</span>
    </Link>
  );
}

function SplashScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-200 border-t-neutral-900" />
    </div>
  );
}

function AuthErrorScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-6">
      <div className="max-w-sm text-center">
        <h2 className="text-base font-semibold text-neutral-900">
          앱을 시작할 수 없어요
        </h2>
        <p className="mt-2 text-xs text-neutral-500">
          환경설정(SUPABASE_URL/ANON_KEY)을 확인해주세요. 잠시 후 다시 시도하면
          해결될 수 있어요.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-5 inline-flex h-10 items-center rounded-lg bg-neutral-900 px-4 text-xs font-semibold text-white"
        >
          다시 시도
        </button>
      </div>
    </div>
  );
}

import { Home, Waves, Settings as SettingsIcon } from "lucide-react";

const TABS = [
  { to: "/", icon: Home, label: "홈" },
  { to: "/history", icon: Waves, label: "추이" },
  { to: "/settings", icon: SettingsIcon, label: "설정" },
] as const;

function BottomTabBar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    // bottom = AdBanner 높이만큼 위로 (없으면 0).
    // padding-bottom 공식: max(safe-area - 광고높이, 8px)
    //  - 광고 있을 때: safe-area는 광고가 흡수 → 8px만 (탭이 광고와 가깝게 붙음)
    //  - 광고 없을 때(유료/웹): safe-area-inset-bottom 그대로 → home indicator 회피
    <nav
      className="fixed inset-x-0 z-40 flex justify-center px-4 pointer-events-none"
      style={{
        bottom: "var(--ad-banner-height, 0px)",
        paddingBottom:
          "max(calc(env(safe-area-inset-bottom) - var(--ad-banner-height, 0px)), 8px)",
      }}
    >
      <div className="pointer-events-auto flex items-center gap-1 rounded-full border border-neutral-200/70 bg-white/90 p-1.5 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)] backdrop-blur-xl">
        {TABS.map((tab) => {
          const active =
            tab.to === "/"
              ? pathname === "/"
              : pathname === tab.to || pathname.startsWith(`${tab.to}/`);
          const Icon = tab.icon;
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={`flex flex-col items-center justify-center gap-1 rounded-full px-5 py-2 transition-all duration-200 ${
                active
                  ? "bg-neutral-900 text-white shadow-sm"
                  : "text-neutral-400 hover:text-neutral-700"
              }`}
            >
              <Icon className="h-[20px] w-[20px]" strokeWidth={2.2} />
              <span className="text-[10px] font-semibold leading-none">
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
